import React, { useState, useMemo } from 'react';
import { 
    Box, 
    Card, 
    CardContent, 
    TextField, 
    Typography, 
    Grid, 
    Alert,
    InputAdornment,
    Chip,
    Pagination,
    Link,
    Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { CutterHistory } from '../../types/cutterHistory';
import { grey, blue, red, orange, green } from '@mui/material/colors';
import { getFormatedStringFromDate } from '../../utils/dateUtils';
import { CopyToClipboard } from '../../common/CopyToClipBoard';
import { ContentCopy } from '@mui/icons-material';

export interface CutterDetailsHistorySalesforceCasesProps {
    cutter: CutterHistory;
}

// Helper function to get status color
const getStatusColor = (status: string): string => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('new') || statusLower.includes('open')) return orange[600];
    if (statusLower.includes('progress') || statusLower.includes('working')) return blue[600];
    if (statusLower.includes('pending')) return orange[400];
    if (statusLower.includes('closed') || statusLower.includes('resolved')) return green[600];
    if (statusLower.includes('cancelled')) return grey[600];
    return grey[800];
};

// Helper function to get priority color
const getPriorityColor = (priority: string): string => {
    const priorityLower = priority?.toLowerCase() || '';
    if (priorityLower.includes('critical') || priorityLower.includes('p1')) return red[700];
    if (priorityLower.includes('high') || priorityLower.includes('p2')) return orange[700];
    if (priorityLower.includes('medium') || priorityLower.includes('p3')) return blue[600];
    if (priorityLower.includes('low') || priorityLower.includes('p4')) return green[600];
    return grey[600];
};

export const CutterDetailsHistorySalesforceCases: React.FC<CutterDetailsHistorySalesforceCasesProps> = ({ 
    cutter
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    
  
    const itemsPerPage = 9;

    const salesforceCases = cutter.salesforceCases || [];

    // Filter cases based on search term
    const filteredCases = useMemo(() => {
        if (!searchTerm.trim()) {
            return salesforceCases;
        }
        
        const lowerSearchTerm = searchTerm.toLowerCase();
        return salesforceCases.filter(salesforceCase => {
            const caseNumber = salesforceCase.caseNumber?.toLowerCase() || '';
            const subject = salesforceCase.subject?.toLowerCase() || '';
            const status = salesforceCase.status?.toLowerCase() || '';
            const description = salesforceCase.description?.toLowerCase() || '';
            
            return caseNumber.includes(lowerSearchTerm) ||
                   subject.includes(lowerSearchTerm) ||
                   status.includes(lowerSearchTerm) ||
                   description.includes(lowerSearchTerm);
        });
    }, [salesforceCases, searchTerm]);

    // Sort cases by created date (newest first)
    const sortedCases = useMemo(() => {
        return [...filteredCases].sort((a, b) => {
            const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
            const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
            return dateB - dateA;
        });
    }, [filteredCases]);

    // Calculate pagination
    const totalPages = Math.ceil(sortedCases.length / itemsPerPage);
    
    // Display cases for current page
    const displayedCases = useMemo(() => {
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedCases.slice(startIndex, endIndex);
    }, [sortedCases, page]);

    // Reset to page 1 when search term changes
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    // Handle page change
    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number): void => {
        setPage(value);
    };


    return (
        <Box sx={{ p: 2 }}>
            {/* Search Box */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Filter by case number, subject, status, or description..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }
                    }}
                    sx={{
                        display: (salesforceCases.length === 0 ? 'none' : 'block')
                        ,'& .MuiOutlinedInput-root': {
                            backgroundColor: 'action.hover',
                        }
                    }}
                />
            </Box>

            {/* Pagination */}
            {sortedCases.length > itemsPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                    <Pagination 
                        count={totalPages} 
                        size="small"
                        color="secondary"
                        page={page} 
                        onChange={handlePageChange}
                    />
                </Box>
            )}

            {/* Cases Grid */}
            {displayedCases.length === 0 ? (
                <Alert severity="info">
                    {salesforceCases.length === 0 
                        ? 'No pending Salesforce cases for this cutter.' 
                        : 'No cases match your search criteria.'}
                </Alert>
            ) : (
                <Grid container spacing={1.5}>
                    {displayedCases.map((salesforceCase) => {
                        const salesforceUrl = `https://lectra.lightning.force.com/lightning/r/Case/${salesforceCase.id}/view`;
                        
                        return (
                            <Grid size={{ xs: 12, md: 4, sm: 6 }} key={salesforceCase.id || salesforceCase.caseNumber}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        transition: 'all 0.2s ease',
                                        backgroundColor: (theme) => theme.palette.mode === 'light' 
                                            ? 'background.paper' 
                                            : '#252525',
                                        '&:hover': {
                                            boxShadow: 4,
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                        {/* Header with Case Number and Status */}
                                        <Stack direction='row' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Link
                                                href={salesforceUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                sx={{ 
                                                    textDecoration: 'none', 
                                                    color: blue[700],
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    fontWeight: 600,
                                                    fontSize: '0.875rem',
                                                    '&:hover': {
                                                        textDecoration: 'underline'
                                                    }
                                                }}
                                            >
                                                {salesforceCase.caseNumber}
                                                <OpenInNewIcon sx={{ fontSize: '0.75rem' }} />
                                            </Link>
                                            <CopyToClipboard
                                                TextToCopy={salesforceCase.caseNumber}
                                                title='Copy case number to clipboard'
                                                AckMessage={`Case number '${salesforceCase.caseNumber}' copied to clipboard`}>
                                                <ContentCopy sx={{ color: 'info', padding: 0, fontSize: '0.6rem' }} titleAccess="Copy case number" />
                                            </CopyToClipboard>
                                            <Chip
                                                label={salesforceCase.status || 'Unknown'}
                                                size="small"
                                                sx={{
                                                    backgroundColor: getStatusColor(salesforceCase.status),
                                                    color: 'white',
                                                    fontWeight: 500,
                                                    fontSize: '0.65rem',
                                                    height: 20
                                                }}
                                            />
                                        </Stack>

                                        {/* Subject */}
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                fontWeight: 500,
                                                fontSize: '0.8rem',
                                                mb: 1,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                minHeight: '2.4em',
                                                color: 'text.primary'
                                            }}
                                        >
                                            {salesforceCase.subject || 'No subject'}
                                        </Typography>

                                        {/* Details */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {/* Priority and Type */}
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {salesforceCase.priority && (
                                                    <Chip
                                                        label={salesforceCase.priority}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: getPriorityColor(salesforceCase.priority),
                                                            color: 'white',
                                                            fontWeight: 500,
                                                            fontSize: '0.65rem',
                                                            height: 18
                                                        }}
                                                    />
                                                )}
                                                {salesforceCase.caseType && (
                                                    <Chip
                                                        label={salesforceCase.caseType}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: '0.65rem',
                                                            height: 18
                                                        }}
                                                    />
                                                )}
                                                {salesforceCase.solutionName && (
                                                    <Chip
                                                        label={salesforceCase.solutionName}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: '0.65rem',
                                                            height: 18,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            {/* Team */}
                                            {salesforceCase.team && (
                                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                                    Team: {salesforceCase.team}
                                                </Typography>
                                            )}

                                            {/* Created Date */}
                                            {salesforceCase.lastModifiedDate && (
                                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                                    Last modified: {getFormatedStringFromDate(new Date(salesforceCase.createdDate))}
                                                </Typography>
                                            )}

                                            {/* Origin */}
                                            {salesforceCase.origin && (
                                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                                    Origin: {salesforceCase.origin}
                                                </Typography>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Box>
    );
};

export default CutterDetailsHistorySalesforceCases;
